import React from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from '@ooh/ui/components/sonner';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { Button } from '@ooh/ui/components/button';
import { Input } from '@ooh/ui/components/input';
import { Textarea } from '@ooh/ui/components/textarea';
import { FormField } from '@ooh/ui/components/form-field';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@ooh/ui/components/command';
import { Popover, PopoverContent, PopoverAnchor } from '@ooh/ui/components/popover';
import type { Client } from '@ooh/contracts';
import { parseApiError, ApiErrorCode } from '@ooh/contracts';
import { useCreateClient, useUpdateClient } from './client-mutations';
import { useClientSuggestions, SUGGESTION_MIN_CHARS } from './client-queries';
import { ClientBrandsField } from './client-brands-field';
import { deriveAcronymPreview, getClientFormDefaults } from './client-dialog-form';
import { useClientBrandRows } from './use-client-brand-rows';
import { createClientFormSchema } from './client-form-schema';
import type { ClientFormValues } from './client-form-schema';

interface ClientDialogProps {
  mode: 'create' | 'edit';
  client?: Client;
  onClose: () => void;
  /**
   * Optional callback invoked after a client is successfully created or updated.
   * Useful for callers that need the resulting entity (e.g. auto-select the new
   * client in a picker after creation from `/offers/new`).
   */
  onSuccess?: (client: Client) => void;
  /**
   * Optional callback invoked when the user selects an *existing* client
   * from the suggestion list in create mode. The dialog closes after calling
   * this and the caller can decide what to do with the selected client
   * (e.g. auto-select it in a picker without re-creating it).
   */
  onSelectExisting?: (client: Client) => void;
}

// eslint-disable-next-line complexity, max-lines-per-function
export function ClientDialog({
  mode,
  client,
  onClose,
  onSuccess,
  onSelectExisting,
}: ClientDialogProps) {
  const { t } = useTranslation('clients');
  const [nameValue, setNameValue] = React.useState(client?.name ?? '');
  const [acronymTouched, setAcronymTouched] = React.useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = React.useState(false);
  const blurTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    return () => {
      if (blurTimerRef.current !== null) {
        clearTimeout(blurTimerRef.current);
      }
    };
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    formState: { errors, isValid, isSubmitting },
  } = useForm<ClientFormValues>({
    resolver: zodResolver(createClientFormSchema(t)) as Resolver<ClientFormValues>,
    mode: 'onChange',
    defaultValues: getClientFormDefaults(client),
  });

  const brandRows = useClientBrandRows(client?.brands ?? [], setValue);
  const resetBrandRows = brandRows.reset;
  const createMutation = useCreateClient();
  const updateMutation = useUpdateClient();
  // Only fetch suggestions in create mode
  const suggestions = useClientSuggestions(mode === 'create' ? nameValue : '');

  const isPending = createMutation.isPending || updateMutation.isPending || isSubmitting;

  function handleMutationError(err: unknown) {
    const apiErr = parseApiError(err);
    if (apiErr.errorCode === ApiErrorCode.CLIENT_ACRONYM_TAKEN) {
      setError('acronym', { message: t('dialog.fields.acronymTaken') });
    } else if (apiErr.statusCode === 409) {
      // Covers CLIENT_NAME_TAKEN plus any other 409 we haven't special-cased —
      // preserves the pre-existing fallback behavior rather than swallowing it.
      setError('name', { message: t('dialog.fields.nameTaken') });
    } else {
      console.error('[ClientDialog] mutation failed', err);
      toast.error(t('actions.saveFailed'));
    }
  }

  // Keep local nameValue in sync with the form field registration
  const { onChange: rhfOnChange, onBlur: rhfOnBlur, ...nameRest } = register('name');
  const { onChange: acronymRhfOnChange, ...acronymRest } = register('acronym');

  function handleNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    setNameValue(e.target.value);
    setSuggestionsOpen(e.target.value.trim().length >= SUGGESTION_MIN_CHARS);
    void rhfOnChange(e);
  }

  function handleAcronymChange(e: React.ChangeEvent<HTMLInputElement>) {
    setAcronymTouched(true);
    void acronymRhfOnChange(e);
  }

  // Create-mode-only live preview: mirrors the server's own suggestion rule
  // as the user types the name, but never overwrites a value the caller
  // has themselves edited. Edit mode starts from the client's real stored
  // acronym and never re-derives it from name edits (client-entity /
  // "Renaming a client leaves its stored acronym alone").
  //
  // `shouldValidate: true` is required here (CI e2e regression, PR #1128
  // fix pass): without it, `isValid` is only recomputed on the next *user*
  // onChange event, matching every other programmatic `setValue` call in
  // this file (see `handleSelectSuggestion`, `syncBrands`). A single
  // `page.fill()` in Playwright dispatches one change event for the whole
  // string, so the acronym preview lands with no further onChange to
  // re-trigger validation — `isValid` stayed stuck at its pre-fill value
  // (false, from the empty defaults) and the save button never enabled.
  // `userEvent.type()` in unit tests fires one event per keystroke, which
  // happened to paper over this: a later keystroke's own validation pass
  // picks up the acronym value the effect set one render earlier.
  React.useEffect(() => {
    if (mode !== 'create' || acronymTouched) return;
    setValue('acronym', deriveAcronymPreview(nameValue), { shouldValidate: true });
  }, [mode, acronymTouched, nameValue, setValue]);

  function handleSelectSuggestion(suggestion: Client) {
    setSuggestionsOpen(false);
    if (onSelectExisting) {
      onSelectExisting(suggestion);
      onClose();
      return;
    }
    // When no onSelectExisting provided, just fill the name field with the selected name
    setNameValue(suggestion.name);
    setValue('name', suggestion.name, { shouldValidate: true });
  }

  const resetDialogState = React.useCallback(
    (nextClient?: Client) => {
      const defaults = getClientFormDefaults(nextClient);

      setNameValue(defaults.name);
      setAcronymTouched(false);
      setSuggestionsOpen(false);
      resetBrandRows(defaults.brands);
      reset(defaults);
    },
    [reset, resetBrandRows],
  );

  React.useEffect(() => {
    resetDialogState(client);
  }, [client, resetDialogState]);

  function normalizeBrands(values: string[] | undefined) {
    return (values ?? []).map((value) => value.trim()).filter(Boolean);
  }

  function onSubmit(data: ClientFormValues) {
    // Create mode: an untouched acronym field holds only a live preview —
    // omit the key entirely so the server's own suggestion (and its
    // numbered fallback when the preview is already taken) runs instead of
    // submitting a value the caller never actually chose. Edit mode always
    // starts from the client's real stored acronym, so it submits
    // unconditionally, the same way notes/brands already do.
    const { acronym, ...rest } = data;
    const payload = {
      ...rest,
      brands: normalizeBrands(data.brands),
      ...(mode === 'edit' || acronymTouched ? { acronym } : {}),
    };

    if (mode === 'create') {
      createMutation.mutate(payload, {
        onSuccess: (created) => {
          toast.success(t('actions.saved'));
          onSuccess?.(created);
          onClose();
        },
        onError: handleMutationError,
      });
    } else if (client) {
      updateMutation.mutate(
        { id: client.id, ...payload },
        {
          onSuccess: (updated) => {
            toast.success(t('actions.saved'));
            onSuccess?.(updated);
            onClose();
          },
          onError: handleMutationError,
        },
      );
    }
  }

  return (
    <ModalDialog
      onClose={onClose}
      closeLabel={t('dialog.close')}
      testId="client-dialog"
      header={
        <h2 className="text-base font-semibold">
          {mode === 'create' ? t('dialog.createTitle') : t('dialog.editTitle')}
        </h2>
      }
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('actions.cancel')}
          </Button>
          <Button
            type="submit"
            // eslint-disable-next-line i18next/no-literal-string
            form="client-form"
            disabled={!isValid || isPending}
            data-testid="client-save-button"
          >
            {isPending ? t('actions.saving') : t('actions.save')}
          </Button>
        </>
      }
    >
      <form
        id="client-form"
        onSubmit={(event) => {
          // Defense-in-depth: this dialog is opened from inside a caller's
          // own <form> (the offer-creation Klient field, quick-add's inline
          // create-draft form) via ModalDialog's portal. React replays
          // bubbling synthetic events through the React tree (not the DOM
          // tree) across portal boundaries, so without stopPropagation the
          // caller's ancestor <form> onSubmit would still fire on save,
          // re-submitting THAT form with its own stale values -- see
          // set-password-dialog.tsx's identical guard for the same
          // portal-bubbling behavior.
          event.stopPropagation();
          void handleSubmit(onSubmit)(event);
        }}
        className="space-y-4"
      >
        <FormField
          testId="client-name-field"
          label={t('dialog.fields.name')}
          htmlFor="client-name"
          required
          error={errors.name?.message}
        >
          {mode === 'create' ? (
            <Popover
              open={suggestionsOpen ? suggestions.length > 0 : false}
              onOpenChange={setSuggestionsOpen}
            >
              <PopoverAnchor asChild>
                <Input
                  id="client-name"
                  placeholder={t('dialog.fields.namePlaceholder')}
                  data-testid="client-name-input"
                  value={nameValue}
                  autoComplete="off"
                  onChange={handleNameChange}
                  onBlur={(e) => {
                    // Small delay to allow suggestion click to register first
                    blurTimerRef.current = setTimeout(() => setSuggestionsOpen(false), 150);
                    void rhfOnBlur(e);
                  }}
                  {...nameRest}
                />
              </PopoverAnchor>
              <PopoverContent
                className="p-0 w-[var(--radix-popover-trigger-width)]"
                align="start"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <Command>
                  <CommandList>
                    <CommandEmpty>{t('dialog.suggestions.noResults')}</CommandEmpty>
                    <CommandGroup heading={t('dialog.suggestions.heading')}>
                      {suggestions.map((suggestion) => (
                        <CommandItem
                          key={suggestion.id}
                          value={suggestion.name}
                          onSelect={() => handleSelectSuggestion(suggestion)}
                          data-testid={`client-suggestion-${String(suggestion.id)}`}
                        >
                          {suggestion.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          ) : (
            <Input
              id="client-name"
              placeholder={t('dialog.fields.namePlaceholder')}
              data-testid="client-name-input"
              {...register('name')}
            />
          )}
        </FormField>

        <FormField
          testId="client-acronym-field"
          label={t('dialog.fields.acronym')}
          htmlFor="client-acronym"
          required
          error={errors.acronym?.message}
        >
          <Input
            id="client-acronym"
            placeholder={t('dialog.fields.acronymPlaceholder')}
            data-testid="client-acronym-input"
            aria-invalid={errors.acronym ? 'true' : 'false'}
            {...acronymRest}
            onChange={handleAcronymChange}
          />
        </FormField>

        <FormField
          testId="client-notes-field"
          label={t('dialog.fields.notes')}
          htmlFor="client-notes"
          error={errors.notes?.message}
        >
          <Textarea
            id="client-notes"
            placeholder={t('dialog.fields.notesPlaceholder')}
            data-testid="client-notes-textarea"
            {...register('notes')}
          />
        </FormField>

        <ClientBrandsField
          rows={brandRows.rows}
          error={errors.brands?.root?.message}
          onAdd={brandRows.add}
          onRemove={brandRows.remove}
          onUpdate={brandRows.update}
        />
      </form>
    </ModalDialog>
  );
}
